import {
  buildForest,
  flattenForest,
  toFolderNode,
} from "@contract-manager/shared";
import { useEffect, useState } from "react";
import type { ApiClient, FolderListing } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { describeError, isSignedOutError } from "../auth/authErrors";
import { useI18n } from "../i18n/I18n";

/**
 * Choosing a folder, in the one place both screens take it from.
 *
 * A flat `<select>` rather than a tree widget: the options carry their path as
 * an indent, which is enough to tell two folders of the same name apart and
 * costs nothing in keyboard or screen-reader behaviour.
 *
 * The API returns only the folders the caller may know about, each carrying
 * what they may do to it. Filtering by that is the caller's job — the upload
 * picker offers the folders you may file into, which is not the same set as
 * the ones you may read.
 */

export interface FoldersState {
  readonly folders: FolderListing[];
  readonly error: string | null;
}

export function useFolders(api: ApiClient): FoldersState {
  const auth = useAuth();
  const { t } = useI18n();
  const [folders, setFolders] = useState<FolderListing[]>([]);
  // Kept untranslated so a language switch neither strands the message in the
  // old language nor refetches the folders.
  const [failure, setFailure] = useState<{ cause: unknown } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const page = await api.listFolders();
        if (!cancelled) {
          setFolders(page.items);
        }
      } catch (caught) {
        if (isSignedOutError(caught)) {
          auth.reportSignedOut();
          return;
        }
        if (!cancelled) {
          setFailure({ cause: caught });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, auth]);

  return {
    folders,
    error: failure === null ? null : describeError(failure.cause, t),
  };
}

export function FolderSelect({
  folders,
  value,
  disabled,
  label,
  onChange,
}: {
  folders: readonly FolderListing[];
  /** Empty string is 未分類 — the contract is in no folder at all. */
  value: string;
  disabled?: boolean;
  label: string;
  onChange: (folderId: string) => void;
}) {
  const { t } = useI18n();
  return (
    <select
      className="ds-select"
      value={value}
      disabled={disabled === true}
      aria-label={label}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">{t("contracts.unfiled")}</option>
      {flattenForest(buildForest(folders.map(toFolderNode))).map((option) => (
        <option key={option.id} value={option.id}>
          {`${"　".repeat(option.depth)}${option.name}`}
        </option>
      ))}
    </select>
  );
}
